'use client'

import { useContext, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { getLangChangerOptions } from 'src/i18n/client/getLangChangerOptions'
import { withCurrentSearchParams } from 'src/i18n/client/withCurrentSearchParams'
import { GlobalContext } from 'src/components/layout/GlobalContext'
import Accordion from 'src/components/design-system/Acordion'
import SlideMenuModal from 'src/components/layout/SlideMenuModal'
import { JournalLogoImage } from 'src/components/journals/components/JournalHeaderShared'
import SearchIcon from 'public/icons/interface/search.svg'
import ArrowIcon from 'public/icons/arrows/arrow-right.svg'
import useJournalDomainRoutes from './useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'

function getRelativeLuminance(hexColor) {
  const normalizedHex = String(hexColor || '')
    .replace('#', '')
    .trim()

  if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return 1
  }

  const [red, green, blue] = [0, 2, 4].map((start) => {
    const channel = parseInt(normalizedHex.slice(start, start + 2), 16) / 255

    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

function getContrastRatio(firstLuminance, secondLuminance) {
  const lighter = Math.max(firstLuminance, secondLuminance)
  const darker = Math.min(firstLuminance, secondLuminance)

  return (lighter + 0.05) / (darker + 0.05)
}

function getReadableTextColor(backgroundColor) {
  const backgroundLuminance = getRelativeLuminance(backgroundColor)
  const whiteContrast = getContrastRatio(backgroundLuminance, 1)

  return whiteContrast >= 4.5 ? '#FFFFFF' : '#000000'
}

function darkenHexColor(hexColor, amount = 0.14) {
  const normalizedHex = String(hexColor || '')
    .replace('#', '')
    .trim()

  if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return hexColor
  }

  const channels = [0, 2, 4].map((start) => {
    const value = parseInt(normalizedHex.slice(start, start + 2), 16)

    return Math.max(0, Math.min(255, Math.round(value * (1 - amount))))
  })

  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

function JournalMobileMenu({ open, links, onClose, locale, journal, searchPath, siteLabel, backgroundColor = null }) {
  const translate = useTranslateEntry()

  return (
    <SlideMenuModal
      open={open}
      id="journal-menu-modal"
      contentLabel={translate('T_MENU_MODAL') || `${siteLabel} menu`}
      onRequestClose={onClose}
      panelClassName="h-screen w-full translate-x-full overflow-hidden opacity-0 xl:w-[40%]"
    >
      <div
        className="flex h-full w-full flex-col gap-32 overflow-y-auto bg-basic-gray1 p-16 py-26 xl:px-24"
        style={backgroundColor ? { backgroundColor } : undefined}
      >
        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-controls="journal-menu-modal"
            aria-expanded="true"
            aria-label={translate('T_CLOSE_MENU') || 'Close menu'}
            className="group relative flex size-61 cursor-pointer items-center justify-center self-center"
          >
            <span
              className="
                    relative block h-1 w-61 bg-basic-white transition-all duration-300
                    before:absolute before:left-0 before:top-1/2 before:h-1 before:w-61 before:-translate-y-11 before:bg-basic-white before:transition-all before:duration-300 before:content-['']
                    after:absolute after:left-0 after:top-1/2 after:h-1 after:w-61 after:translate-y-10 after:bg-basic-white after:transition-all after:duration-300 after:content-['']
                    group-aria-expanded:bg-transparent group-aria-expanded:before:translate-y-0 group-aria-expanded:before:rotate-45
                    group-aria-expanded:after:translate-y-0 group-aria-expanded:after:-rotate-45
                  "
            />
          </button>
        </div>

        <nav aria-label={`${siteLabel} mobile`}>
          <JournalSearchForm
            locale={locale}
            journalUuid={journal?.uuid}
            searchPath={searchPath}
            className="relative mb-32 w-full"
          />

          <Accordion type="list">
            {links.map((item) => {
              const itemKey = `${item.href || ''}-${item.label || ''}`
              const childLinks = Array.isArray(item.children) ? item.children : []

              if (childLinks.length > 0) {
                return (
                  <Accordion.Item id={`journal-menu-${itemKey}`} key={itemKey}>
                    {item.href ? (
                      <Accordion.Trigger variant="link" href={item.href} linkOnClick={onClose}>
                        {item.label}
                      </Accordion.Trigger>
                    ) : (
                      <Accordion.Trigger variant="button">{item.label}</Accordion.Trigger>
                    )}
                    <Accordion.Content>
                      <ul className="flex list-none flex-col gap-y-16 py-16 pl-16">
                        {childLinks.map((child) => (
                          <li key={`${child.href || ''}-${child.label || ''}`}>
                            <Link
                              href={child.href || '#'}
                              onClick={onClose}
                              className="block h4 h4--caps text-basic-white/80 transition-colors supports-hover:hover:text-dark-blue-dark-blue5"
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </Accordion.Content>
                  </Accordion.Item>
                )
              }

              return (
                <Accordion.Item id={`journal-menu-${itemKey}`} key={itemKey}>
                  <Accordion.Trigger variant="only-link" href={item.href || '#'} linkOnClick={onClose}>
                    {item.label}
                  </Accordion.Trigger>
                </Accordion.Item>
              )
            })}
          </Accordion>
        </nav>
      </div>
    </SlideMenuModal>
  )
}

function JournalLangChanger({ locale }) {
  const pathname = usePathname()
  const router = useRouter()
  const { langChangerState } = useContext(GlobalContext)
  const [open, setOpen] = useState(false)
  const options = getLangChangerOptions(langChangerState, locale)

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  useEffect(() => {
    setOpen(false)
  }, [pathname, locale])

  return (
    <div className="relative flex w-fit items-center">
      {options.length > 0 ? (
        <button
          type="button"
          onClick={() => setOpen((isOpen) => !isOpen)}
          aria-expanded={open}
          aria-controls="journal-lang-menu"
          className="group flex w-fit cursor-pointer items-center justify-start gap-x-2"
        >
          <span className="h4 h4--caps pb-2 text-basic-black transition-colors">{locale}</span>
          <span className="flex size-18 rotate-90 items-center justify-center rounded-full border border-basic-black transition-colors group-aria-expanded:-rotate-90">
            <ArrowIcon className="text-basic-black" />
          </span>
        </button>
      ) : (
        <span className="h4 h4--caps pb-2 text-basic-black transition-colors">{locale}</span>
      )}
      {open && options.length > 0 && (
        <ul
          id="journal-lang-menu"
          className="absolute right-0 top-[120%] z-10 w-fit border border-basic-black bg-basic-white px-0 py-0"
        >
          {options.map((option) => (
            <li key={option.locale}>
              <Link
                href={option.path}
                // `href` zostaje bez query (inaczej byłaby niezgodność hydracji), więc parametry
                // z bieżącego adresu dokładamy dopiero przy kliknięciu.
                onClick={(event) => {
                  setOpen(false)

                  const targetPath = withCurrentSearchParams(option.path, option.locale)

                  if (targetPath === option.path || event.metaKey || event.ctrlKey || event.shiftKey) return

                  event.preventDefault()
                  router.push(targetPath)
                }}
                className="block w-full p-6 h4 h4--caps text-basic-black supports-hover:hover:underline"
              >
                {option.locale}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function JournalSearchForm({
  locale,
  journalUuid,
  searchPath: configuredSearchPath,
  className = 'relative hidden w-322 xl:block',
}) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const searchPath = configuredSearchPath || buildLocalizedPath(journalRoutes.SEARCH_RESULTS, locale)

  const handleSubmit = (event) => {
    event.preventDefault()

    const formData = new FormData(event.currentTarget)
    const query = String(formData.get('query') || '').trim()

    if (!query) {
      return
    }

    const params = new URLSearchParams({ query })
    if (journalUuid) {
      params.set('journalUuid', journalUuid)
    }

    window.location.href = `${searchPath}?${params.toString()}`
  }

  return (
    <form className={className} role="search" onSubmit={handleSubmit}>
      <input
        name="query"
        aria-label={translate('T_SEARCH')}
        className="h-40 w-full rounded-4 bg-basic-gray10 pl-16 pr-48 body-b3-normal outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-functional-focus-color"
      />
      <button
        type="submit"
        className="absolute right-12 top-1/2 flex size-21 -translate-y-1/2 items-center justify-center"
        aria-label={translate('T_SEARCH')}
      >
        <SearchIcon className="size-21 [&_path]:stroke-basic-black" />
      </button>
    </form>
  )
}

function JournalDesktopNavItem({ item, primaryColor }) {
  const translate = useTranslateEntry()
  const dropdownRef = useRef(null)
  const [alignEnd, setAlignEnd] = useState(false)
  const childLinks = Array.isArray(item.children) ? item.children : []
  const hasChildren = childLinks.length > 0
  const linkClass =
    "h4 h4--caps relative block h-fit w-fit whitespace-nowrap p-10 text-[var(--journal-nav-text)] after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:bg-[var(--journal-nav-text)] after:transition-[width] after:content-[''] supports-hover:hover:after:w-full"

  const updateAlignment = () => {
    const dropdown = dropdownRef.current
    const trigger = dropdown?.parentElement

    if (!dropdown || !trigger) {
      return
    }

    // Measure from the trigger's left edge so the result does not depend on the
    // dropdown's current alignment (prevents flip-flopping once aligned right).
    const triggerLeft = trigger.getBoundingClientRect().left
    const dropdownWidth = dropdown.offsetWidth
    const viewportEdge = window.innerWidth - 16

    setAlignEnd(triggerLeft + dropdownWidth > viewportEdge)
  }

  if (!hasChildren) {
    return (
      <li className="shrink-0">
        {item.href ? (
          <Link href={item.href} className={linkClass}>
            {item.label}
          </Link>
        ) : (
          <span className={`${linkClass} cursor-default`}>{item.label}</span>
        )}
      </li>
    )
  }

  return (
    <li
      className="group relative flex h-full shrink-0 items-center"
      onMouseEnter={updateAlignment}
      onFocus={updateAlignment}
    >
      <div className="flex items-center rounded-4 transition-colors group-hover:bg-black/10 group-focus-within:bg-black/10">
        {item.href ? (
          <Link href={item.href} className={`${linkClass} pr-4`}>
            {item.label}
          </Link>
        ) : (
          <span className={`${linkClass} cursor-default pr-4`}>{item.label}</span>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          aria-label={translate('T_EXPAND') || 'Expand'}
          className="mr-6 flex size-18 shrink-0 items-center justify-center text-[var(--journal-nav-text)]"
        >
          <ArrowIcon className="size-12 rotate-90 transition-transform duration-300 group-hover:-rotate-90 group-focus-within:-rotate-90" />
        </button>
      </div>
      <ul
        ref={dropdownRef}
        className={`invisible absolute top-full z-30 flex min-w-[15rem] translate-y-4 list-none flex-col p-0 opacity-0 shadow-[0px_12px_30px_rgba(0,0,0,0.25)] transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 ${
          alignEnd ? 'right-0' : 'left-0'
        }`}
        style={{ backgroundColor: darkenHexColor(primaryColor) }}
      >
        {childLinks.map((child) => (
          <li key={`${child.href || ''}-${child.label || ''}`}>
            <Link
              href={child.href || '#'}
              className="block whitespace-normal px-20 py-14 h4 h4--caps leading-tight text-[var(--journal-nav-text)] transition-colors supports-hover:hover:bg-basic-white/10"
            >
              {child.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  )
}

export function DomainSiteTopbar({
  journal,
  title,
  navLinks,
  primaryColor,
  locale,
  logos = null,
  homeHref: configuredHomeHref = null,
  searchPath = null,
  siteLabel = 'Journal',
  mobileMenuColor = null,
}) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [openMenu, setOpenMenu] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const topbarRef = useRef(null)

  // Belka jest `fixed`, a jej wysokość zależy od breakpointu — mierzymy ją dla treści.
  useEffect(() => {
    const element = topbarRef.current

    if (!element) return

    const publishHeight = () => {
      document.documentElement.style.setProperty('--header-height', `${element.offsetHeight}px`)
    }

    publishHeight()

    const observer = new ResizeObserver(publishHeight)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])
  const navTextColor = getReadableTextColor(primaryColor)
  const homeHref = configuredHomeHref || buildLocalizedPath(journalRoutes.HOME, locale) || '/'
  const resolvedLogos = Array.isArray(logos) ? logos.filter(Boolean) : [journal?.logo1, journal?.logo2].filter(Boolean)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <div
        ref={topbarRef}
        className={`fixed left-0 top-0 z-[999] w-full bg-basic-white transition-all duration-500 main-header ${
          scrolled ? 'bg-basic-white/70 shadow-[0px_4px_85.1px_0px_#00000040] backdrop-blur-[13.6px]' : ''
        }`}
      >
        <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-[1fr_auto] items-center gap-x-12 gap-y-16 p-16 d:px-40 d:py-24 xl:grid-cols-[1fr_auto]">
          <Link href={homeHref} className="flex w-fit flex-wrap items-center gap-16 d:gap-36" aria-label={title}>
            {resolvedLogos.length > 0 ? (
              resolvedLogos.map((media, index) => (
                <JournalLogoImage
                  key={media?.uuid || media?.url || index}
                  media={media}
                  alt={title}
                  className={index === 0 ? 'max-h-42 d:max-h-76' : 'max-h-42 d:max-h-61'}
                />
              ))
            ) : (
              <span className="h3-bold text-basic-black">{title}</span>
            )}
          </Link>

          <div className="col-start-2 row-start-1 flex items-center justify-end gap-16">
            <JournalSearchForm locale={locale} journalUuid={journal?.uuid} searchPath={searchPath} />
            <span className="hidden h-36 w-1 bg-basic-gray7 xl:block" />
            <JournalLangChanger locale={locale} />
            <button
              type="button"
              onClick={() => setOpenMenu((isOpen) => !isOpen)}
              aria-controls="journal-menu-modal"
              aria-expanded={openMenu}
              aria-label={translate('T_OPEN_MENU') || 'Open menu'}
              className="group relative flex h-23 w-53 cursor-pointer flex-col items-start justify-between t:w-61 xl:hidden"
            >
              <span className="block h-1 w-full bg-basic-black transition-all duration-500 group-hover:w-1/3" />
              <span className="block h-1 w-full bg-basic-black transition-all duration-500 group-hover:w-2/3" />
              <span className="block h-1 w-full bg-basic-black duration-500" />
            </button>
          </div>
        </div>
        <nav
          className="hidden px-16 d:block d:px-40"
          style={{ backgroundColor: primaryColor, '--journal-nav-text': navTextColor }}
          aria-label={siteLabel}
        >
          <ul className="mx-auto flex h-60 w-auto min-w-fit max-w-screen-2xl list-none items-center gap-24 p-0 d:justify-end">
            {navLinks.map((item) => (
              <JournalDesktopNavItem
                key={`${item.href || ''}-${item.label || ''}`}
                item={item}
                primaryColor={primaryColor}
              />
            ))}
          </ul>
        </nav>
        <div className="h-4 w-full d:hidden" style={{ backgroundColor: primaryColor }} />
      </div>
      <JournalMobileMenu
        open={openMenu}
        links={navLinks}
        onClose={() => setOpenMenu(false)}
        locale={locale}
        journal={journal}
        searchPath={searchPath}
        siteLabel={siteLabel}
        backgroundColor={mobileMenuColor}
      />
    </>
  )
}

export default function DomainJournalTopbar(props) {
  return <DomainSiteTopbar {...props} />
}
